import axios from 'axios'
import { getEpsBaseUrl } from './eps-base-url'

// Use the default axios instance to inherit global interceptors from main.js
// The global interceptor automatically adds Authorization headers to all requests

// EPS API Response Types
export type EpsLocationResponse = {
  id: string
  locationId: number
  year: number
  zipCode: string
  state: string
  county: string
  countyFipsCode: string
  ratingAreaNumeric: number
  createdAt: string
  updatedAt: string
}

// Legacy Locations Service Response Types (for mapping)
export type LegacyLocationResponse = {
  Id: string
  County: string
  CountyFipsCode: string
  RatingAreaNumeric: string
  StateAndRatingAreaNumeric: string
  StateProvince: string
  ZipCode: string
}

/**
 * Maps EPS location response to legacy format for backward compatibility
 */
export const mapEpsToLegacyFormat = (epsLocation: EpsLocationResponse): LegacyLocationResponse => {
  return {
    Id: epsLocation.id,
    County: epsLocation.county,
    CountyFipsCode: epsLocation.countyFipsCode,
    RatingAreaNumeric: epsLocation.ratingAreaNumeric.toString(),
    StateAndRatingAreaNumeric: `${epsLocation.state}${epsLocation.ratingAreaNumeric}`,
    StateProvince: epsLocation.state,
    ZipCode: epsLocation.zipCode,
  }
}

/**
 * Searches locations using the new EPS API
 * @param year - The year for the search
 * @param zipCodes - Comma-separated zip codes or single zip code
 * @returns Promise<EpsLocationResponse[]>
 */
export const searchEpsLocations = async (
  year: number,
  zipCodes: string
): Promise<EpsLocationResponse[]> => {
  try {
    const baseUrl = getEpsBaseUrl()
    const url = `${baseUrl}/locations/search`
    
    const response = await axios.get(url, {
      params: {
        year,
        zipCodes,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    return response.data
  } catch (error) {
    console.error('EPS API Error:', error)
    throw new Error('Failed to fetch locations from EPS API')
  }
}

/**
 * Searches locations using EPS API and returns in legacy format
 * @param year - The year for the search
 * @param zipCodes - Comma-separated zip codes or single zip code
 * @returns Promise<LegacyLocationResponse[]>
 */
export const searchEpsLocationsLegacyFormat = async (
  year: number,
  zipCodes: string
): Promise<LegacyLocationResponse[]> => {
  const epsLocations = await searchEpsLocations(year, zipCodes)
  return epsLocations.map(mapEpsToLegacyFormat)
}
